/* Кадр студии на движке шаблона (engine-kit, план §5.2). Один на кадр ServerStudio, рядом с колонкой React, не внутри неё.
   Кадр всегда держит свой сервис заказа (опрос идёт и у спрятанного кадра). Движок шаблона (window[global]) один на страницу,
   поэтому привязан он только к активному кадру: стал активным — привязка рисования к своему .st-eng, bind движка, вход в
   engineHost, перехват «назад» и open(хозяин, {}) без onClose; перестал или снят — close() (тихий: onClose не задан),
   снятие привязок и очистка хозяина (узлы движок ищет по глобальным id, они должны быть только у активного кадра).
   Эффекты компоновки детей идут раньше эффекта смены вида ServerStudio, поэтому движок рисует до enterView; все очистки
   эффектов компоновки коммита идут до их установок — прежний кадр отвязан раньше, чем привязан новый. В StrictMode
   (page-module.tsx) open и close повторяются без вреда, close никуда не уводит. React хозяина не наполняет.
   Знак заказа над меню публикует кадр (useOrderBadge, вид — kind экрана или его разрешитель): «показан», пока кадр активен.
   Движок экрана ищется по каноническому имени и псевдонимам (engineEntry: circle, lipsync → who; swap → dance).
   «Назад» внутри экрана (каталог ← шаблон рилса) кадр сначала предлагает привязке (bound.back): и кнопке «назад» движка, и «назад»
   раздела. Готовность первой отрисовки (bound.ready) кадр отдаёт смене вида ServerStudio: она ждёт её не дольше READY_MS.
   Экран с режимами (EngineScreen.modes: photo, video) — ModeServices рядом с хозяином: сервисы режимов, их знаки и
   EngineBinding.modes; любой их вид — тоже changed(). */
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { UserAvatar } from '../../../api/people';
import type { StudioApp } from '../studio-api';
import { bindStudioPaint } from '../paint';
import { engineEntry, engineGlobal, engineHost, engineKind, type EngineBound, type EngineModes } from './engine-host';
import { ModeServices } from './mode-group';
import { useOrderBadge } from './use-order-badge';
import { useOrderService } from './use-order-service';
import './engine.css';

/** Сколько смена вида ждёт готовность первой отрисовки движка (bound.ready), мс */
export const READY_MS = 2500;

export interface EngineFrameProps {
  /** Ключ кадра ServerStudio (`${app.id}:${revision}:e`) */
  frameKey: string;
  /** Экран входа (app.entry.screen): движок — ENGINES по каноническому имени или псевдониму */
  screen: string;
  app: StudioApp;
  avatar: UserAvatar | null;
  initialInputs?: Record<string, unknown>;
  /** Экран открыт кнопкой «Попробовать» листа шаблона: у кружка формат и название задаёт шаблон (APP-SPEC-4 §6.3) */
  templateEntry?: boolean;
  active: boolean;
  /** «Назад» раздела (ServerStudio.back) */
  onBack(): void;
  /** Обработчик «назад» формы (formBack ServerStudio): у активного кадра — шаг назад внутри экрана (bound.back), иначе закрыть
      движок и пустить «назад» дальше */
  registerBack?(handler: () => boolean): () => void;
  /** Готовность активного кадра для смены вида ServerStudio: ожидание (готовность или READY_MS) или null — ждать нечего */
  registerGate?(gate: () => Promise<void> | null): () => void;
  hostRef?(el: HTMLDivElement | null): void;
}

export function EngineFrame(p: EngineFrameProps) {
  const { svc, view } = useOrderService({ app: p.app, avatar: p.avatar, initialInputs: p.initialInputs });
  const engine = engineEntry(p.screen)?.engine ?? null,
    app = p.app;
  /* у экрана с режимами знак сервиса кадра публикует ModeServices (показан, пока режим кадра текущий) */
  useOrderBadge(svc, engine?.modes ? null : s => engineKind(engine, s, app), p.active);
  const host = useRef<HTMLDivElement | null>(null);
  const bound = useRef<EngineBound | null>(null);
  /** Режимы кадра (ModeServices ставит их до привязки) */
  const modes = useRef<EngineModes | null>(null);
  const setModes = useCallback((next: EngineModes | null) => {
    modes.current = next;
  }, []);
  const changed = useCallback(() => {
    bound.current?.changed();
  }, []);
  /** Кадр привязан к движку сейчас (active() привязки) */
  const bonded = useRef(false);
  /** Пропсы последнего зафиксированного рендера: «назад» ServerStudio новый на каждом рендере */
  const latest = useRef(p);
  useLayoutEffect(() => {
    latest.current = p;
  });
  const setHost = useCallback((el: HTMLDivElement | null) => {
    host.current = el;
    latest.current.hostRef?.(el);
  }, []);
  const { active, screen, frameKey } = p;
  useLayoutEffect(() => {
    const el = host.current,
      engine = engineEntry(screen)?.engine ?? null;
    if (!active || !el || !engine) return;
    bonded.current = true;
    const releasePaint = bindStudioPaint(true, el);
    const { app, initialInputs, templateEntry } = latest.current;
    const binding = engine.bind({
      key: frameKey,
      svc,
      app,
      initialInputs,
      templateEntry: !!templateEntry,
      ...(modes.current ? { modes: modes.current } : {}),
      back: () => latest.current.onBack(),
      active: () => bonded.current,
    });
    bound.current = binding;
    engineHost.enter(screen);
    /* «назад» движка (data-<prefix>-back) — шаг назад внутри экрана (bound.back) или «назад» раздела: окно в фазе захвата слышит
       нажатие раньше слушателя движка на документе, и до движка оно не доходит (в том числе пока вид уходит и back() раздела его
       не копит) — внутренний шаг делает сама привязка */
    const mark = `[data-${engine.prefix}-back]`;
    const intercept = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target.closest(mark) : null;
      if (!target || !el.contains(target)) return;
      event.preventDefault();
      event.stopPropagation();
      if (binding.back?.()) return;
      latest.current.onBack();
    };
    window.addEventListener('click', intercept, true);
    const unregister = latest.current.registerBack?.(() => {
      if (binding.back?.()) return true;
      engineGlobal(screen)?.close();
      return false;
    });
    engineGlobal(screen)?.open(el, {});
    /* готовность первой отрисовки: ожидание до готовности или READY_MS; после него (и у привязки без ready) ждать нечего */
    let gate: Promise<void> | null = null,
      timer = 0;
    const ready = binding.ready?.();
    if (ready) {
      const pending: Promise<void> = Promise.race([
        Promise.resolve(ready).then(
          () => undefined,
          () => undefined,
        ),
        new Promise<void>(resolve => {
          timer = window.setTimeout(resolve, READY_MS);
        }),
      ]).then(() => {
        window.clearTimeout(timer);
        if (gate === pending) gate = null;
      });
      gate = pending;
    }
    const unregisterGate = latest.current.registerGate?.(() => gate);
    return () => {
      window.clearTimeout(timer);
      gate = null;
      unregisterGate?.();
      bonded.current = false;
      engineGlobal(screen)?.close();
      binding.release();
      bound.current = null;
      window.removeEventListener('click', intercept, true);
      unregister?.();
      engineHost.leave(screen);
      el.replaceChildren();
      // классы движка (ap, leave/enter swap'а) уходят вместе с его разметкой
      el.className = 'st-eng';
      if (!engineHost.side()) document.getElementById('bot')?.classList.remove('sd');
      releasePaint();
    };
  }, [active, screen, frameKey, svc]);
  /* каждый зафиксированный вид сервиса — движку (форма, цена, заказ) */
  useEffect(() => {
    bound.current?.changed();
  }, [view]);
  return (
    <>
      <div ref={setHost} className="st-eng" hidden={!active} inert={!active} />
      {engine?.modes && (
        <ModeServices
          app={p.app}
          svc={svc}
          view={view}
          avatar={p.avatar}
          initialInputs={p.initialInputs}
          active={active}
          engine={engine}
          onModes={setModes}
          onView={changed}
        />
      )}
    </>
  );
}
